<template>
    <div class="ui-page">
        <ui-page-header title="Outros processos" subtitle="Processos judiciais e administrativos fora do fluxo de Impugnações que exigem providências da Administração Judicial.">
            <!-- Incidentes de credito capturados no DJEN e ainda nao cadastrados -->
            <djen-new-button base="/api/admin/other-proceedings" @changed="reload" />
            <v-btn outlined color="primary" class="mr-2" :loading="dataList.loading" @click="reload">
                <v-icon left small>mdi-refresh</v-icon>
                Atualizar
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" color="primary" depressed to="/outros-processos/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <!-- Cards de resumo: cada um aplica um filtro ao clicar -->
        <div class="op-cards">
            <button v-for="card in cards" :key="card.key" type="button" class="op-card" :class="{ 'op-card--active': card.active }" @click="applyCard(card)">
                <span class="op-card__label">{{ card.label }}</span>
                <span class="op-card__value" :class="`op-card__value--${card.tone}`">{{ card.value }}</span>
            </button>
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.term" placeholder="Buscar por processo, assunto, parte, CPF ou CNPJ" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details clearable class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="dataList.filter.client_id" :items="catalogs.clients" item-value="id" item-text="name" placeholder="Todos os casos"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" no-data-text="Nenhum caso encontrado" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.process_type" :items="processTypes" item-value="id" item-text="name" placeholder="Todos os tipos"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.status" :items="statuses" item-value="id" item-text="name" placeholder="Todos os status"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-autocomplete v-model="dataList.filter.responsible_admin_id" :items="catalogs.admins" item-value="id" item-text="name" placeholder="Todos os responsáveis"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.due_state" :items="dueStates" item-value="id" item-text="name" placeholder="Todas as situações de prazo"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count" :options.sync="dataList.options"
                :loading="dataList.loading" disable-sort hide-default-footer no-data-text="Nenhum processo encontrado para os filtros"
                loading-text="Carregando dados..." class="ui-clickable-rows" @click:row="openItem">

                <template v-slot:[`item.process_number`]="{ item }">
                    <div class="ui-cell-main">{{ item.process_number || 'Sem número' }}</div>
                    <div class="ui-cell-sub">{{ item.subject }}</div>
                </template>

                <template v-slot:[`item.client_name`]="{ item }">
                    {{ item.client_name || '—' }}
                </template>

                <template v-slot:[`item.process_type`]="{ item }">
                    <div>{{ item.process_type_name }}</div>
                    <div class="ui-cell-sub">{{ item.nature_name }}</div>
                </template>

                <template v-slot:[`item.responsible_name`]="{ item }">
                    <span v-if="item.responsible_name">{{ item.responsible_name }}</span>
                    <span v-else class="op-danger">Sem responsável</span>
                </template>

                <template v-slot:[`item.last_movement_at`]="{ item }">
                    {{ item.last_movement_at ? formatDate(item.last_movement_at) : '—' }}
                </template>

                <template v-slot:[`item.next_due_at`]="{ item }">
                    <ui-badge v-if="item.next_due_at" :text="formatDate(item.next_due_at)" :tone="toneOf(dueStates, item.due_state)" />
                    <span v-else class="ui-muted">Sem prazo</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.status_name" :tone="toneOf(statuses, item.status)" />
                    <div v-if="item.pending_tasks_count || item.unanswered_communications_count" class="ui-cell-sub">
                        <span v-if="item.pending_tasks_count">{{ item.pending_tasks_count }} providência(s)</span>
                        <span v-if="item.pending_tasks_count && item.unanswered_communications_count"> · </span>
                        <span v-if="item.unanswered_communications_count">{{ item.unanswered_communications_count }} sem resposta</span>
                    </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="rowActions(item)" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.size"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import { processTypes, statuses, dueStates, toneOf } from "@/common/data/other-proceeding.data";
import DjenNewButton from "@/components/djen/DjenNewButton.vue";

export default {
    name: "OtherProceedingList",

    components: { DjenNewButton },

    data: () => ({
        processTypes,
        statuses,
        dueStates,
        catalogs: { clients: [], admins: [] },
        dashboard: { active: 0, overdue: 0, upcoming: 0, unanswered_communications: 0, no_responsible: 0 },
        dataList: {
            filter: { term: "", client_id: null, process_type: null, status: null, responsible_admin_id: null, due_state: null, active_only: false, no_responsible: false, has_unanswered_communications: false },
            headers: [
                { text: "Processo / assunto", value: "process_number", width: 260 },
                { text: "Caso", value: "client_name", width: 180 },
                { text: "Tipo e natureza", value: "process_type", width: 170 },
                { text: "Tribunal ou órgão", value: "tribunal_or_agency", width: 150 },
                { text: "Responsável", value: "responsible_name", width: 150 },
                { text: "Última movimentação", value: "last_movement_at", width: 130 },
                { text: "Próximo prazo", value: "next_due_at", width: 130 },
                { text: "Status e alertas", value: "status", width: 190 },
                { text: "", value: "actions", width: 56, sortable: false, align: "end" },
            ],
            data: [],
            count: 0,
            loading: false,
            options: { page: 1, size: 25 },
        },
    }),

    computed: {
        hasFilters() {
            const f = this.dataList.filter;
            return !!(f.term || f.client_id || f.process_type || f.status || f.responsible_admin_id || f.due_state || f.active_only || f.no_responsible || f.has_unanswered_communications);
        },
        cards() {
            const f = this.dataList.filter;
            return [
                { key: "active", label: "Processos em acompanhamento", value: this.dashboard.active, tone: "neutral", active: f.active_only && !f.due_state && !f.no_responsible && !f.has_unanswered_communications },
                { key: "overdue", label: "Prazos vencidos", value: this.dashboard.overdue, tone: this.dashboard.overdue ? "danger" : "neutral", active: f.due_state === "overdue" },
                { key: "upcoming", label: "Vencendo em 7 dias", value: this.dashboard.upcoming, tone: this.dashboard.upcoming ? "warning" : "neutral", active: f.due_state === "upcoming" },
                { key: "communications", label: "Comunicações sem resposta", value: this.dashboard.unanswered_communications, tone: this.dashboard.unanswered_communications ? "warning" : "neutral", active: f.has_unanswered_communications },
                { key: "owner", label: "Processos sem responsável", value: this.dashboard.no_responsible, tone: this.dashboard.no_responsible ? "danger" : "neutral", active: f.no_responsible },
            ];
        },
    },

    created() {
        this.loadCatalogs();
        this.reload();
    },

    methods: {
        toneOf,

        formatDate(value) {
            return value ? this.$moment(String(value).slice(0, 10)).format('DD/MM/YYYY') : '';
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 500),

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        clearFilters() {
            this.dataList.filter = { term: "", client_id: null, process_type: null, status: null, responsible_admin_id: null, due_state: null, active_only: false, no_responsible: false, has_unanswered_communications: false };
            this.applyFilters();
        },

        // Card clicado: aplica o filtro correspondente (clicar de novo desfaz).
        applyCard(card) {
            const f = this.dataList.filter;
            const wasActive = card.active;
            f.due_state = null;
            f.no_responsible = false;
            f.has_unanswered_communications = false;
            f.active_only = false;
            if (!wasActive) {
                if (card.key === "active") f.active_only = true;
                if (card.key === "overdue") f.due_state = "overdue";
                if (card.key === "upcoming") f.due_state = "upcoming";
                if (card.key === "communications") f.has_unanswered_communications = true;
                if (card.key === "owner") f.no_responsible = true;
            }
            this.applyFilters();
        },

        setPageSize(size) {
            this.dataList.options.size = size;
            this.applyFilters();
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        reload() {
            this.load();
            this.loadDashboard();
        },

        async load() {
            this.dataList.loading = true;
            try {
                const search = {};
                Object.entries(this.dataList.filter).forEach(([key, value]) => {
                    if (value !== null && value !== "" && value !== false) search[key] = value;
                });
                const result = await axios.get('/api/admin/other-proceedings', {
                    params: { page: this.dataList.options.page, limit: this.dataList.options.size, search: JSON.stringify(search) }
                });
                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadDashboard() {
            try {
                const params = this.dataList.filter.client_id ? { client_id: this.dataList.filter.client_id } : {};
                const result = await axios.get('/api/admin/other-proceedings/dashboard', { params });
                this.dashboard = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            }
        },

        async loadCatalogs() {
            try {
                const result = await axios.get('/api/admin/other-proceedings/catalogs');
                this.catalogs = { clients: result.data.content.clients, admins: result.data.content.admins };
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            }
        },

        openItem(item, tab = null, create = false) {
            const query = {};
            if (tab) query.tab = tab;
            if (create) query.create = "1";
            this.$router.push({ path: `/outros-processos/${item.id}`, query });
        },

        rowActions(item) {
            return [
                { icon: "mdi-eye-outline", text: "Visualizar", handler: () => this.openItem(item) },
                { icon: "mdi-timeline-plus-outline", text: "Registrar movimentação", handler: () => this.openItem(item, "movements", true) },
                { icon: "mdi-clipboard-check-outline", text: "Criar providência", handler: () => this.openItem(item, "tasks", true) },
            ];
        },
    },
};
</script>

<style scoped>
.op-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin-bottom: 16px; }
.op-card { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; text-align: left; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
.op-card:hover { border-color: #98a2b3; }
.op-card--active { border-color: #27306b; box-shadow: 0 0 0 1px #27306b inset; }
.op-card__label { font-size: 12.5px; color: #475467; }
.op-card__value { font-size: 20px; font-weight: 700; color: #101828; }
.op-card__value--danger { color: #dc2626; }
.op-card__value--warning { color: #d97706; }
.op-danger { color: #dc2626; }
</style>
